import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Modal, message, Spin, Tabs } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { pw9208Api } from '../../services/pw9208Api';
import { usePW9208Mode } from './hooks/usePW9208Mode';
import { usePW9208Record } from './hooks/usePW9208Record';
import { usePW9208Navigation } from './hooks/usePW9208Navigation';
import { usePW9208Lookup } from './hooks/usePW9208Lookup';
import { useModulePermission } from '../../hooks/usePermission';
import { Modules } from '../../constants/permissions';
import PermissionGuard from '../../components/PermissionGuard';
import ActionBar from './components/ActionBar';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2Approval from './components/Tab2Approval';
import './PW9208Form.css';

const PW9208Form: React.FC = () => {
  const navigate = useNavigate();
  const perms = useModulePermission(Modules.PW9208);

  // Hooks
  const { mode, setMode, isEditing } = usePW9208Mode();
  const { names, resolveSingleName, resolveAllNames, clearNames } = usePW9208Lookup();
  const {
    record, formData, loading,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    getFieldError, clearFieldErrors,
  } = usePW9208Record();

  // Wrap loadRecord to also resolve names
  const loadRecordWithNames = useCallback(async (id: string) => {
    const result = await loadRecord(id);
    if (result) {
      await resolveAllNames(result);
    }
    return result;
  }, [loadRecord, resolveAllNames]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = usePW9208Navigation(loadRecordWithNames);

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecordWithNames(ids[0]);
        }
      } catch {
        // ignore
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handlers
  const handleAdd = useCallback(() => {
    if (isEditing) {
      handleSave(mode, (result) => {
        applyResult(result);
        setMode('browse');
        loadList().then(ids => {
          const idx = ids.indexOf(result.f90m_01);
          if (idx >= 0) setCurrentIdx(idx);
        });
      });
    } else {
      clearNames();
      clearFieldErrors();
      resetForAdd();
      setMode('add');
    }
  }, [isEditing, mode, handleSave, applyResult, setMode, clearNames, clearFieldErrors, resetForAdd, loadList, setCurrentIdx]);

  const handleModify = useCallback(() => {
    if (isEditing) {
      revertToRecord();
      setMode('browse');
    } else {
      if (!record) return;
      setMode('edit');
    }
  }, [isEditing, record, revertToRecord, setMode]);

  const handleDeleteRecord = useCallback(() => {
    if (!record) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除資遣單「${record.f90m_01}」？`,
      okText: '刪除', okType: 'danger', cancelText: '取消',
      onOk: async () => {
        const ok = await handleDelete();
        if (ok) {
          const ids = await loadList();
          if (ids.length > 0) {
            const newIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(newIdx);
            await loadRecordWithNames(ids[newIdx]);
          } else {
            setCurrentIdx(-1);
            clearNames();
            resetForAdd();
          }
        }
      },
    });
  }, [record, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecordWithNames, clearNames, resetForAdd]);

  const handleNavTo = useCallback((idx: number) => {
    navTo(idx, isEditing);
  }, [navTo, isEditing]);

  const handleSearch = useCallback(async (keyword: string) => {
    if (!keyword.trim()) return;
    try {
      const res = await pw9208Api.search(keyword.trim());
      if ((res.data || []).length === 0) {
        message.info('查無資料');
        return;
      }
      const ids = (res.data || []).map(r => r.f90m_01);
      setCurrentIdx(0);
      await loadRecordWithNames(ids[0]);
    } catch {
      message.error('查詢失敗');
    }
  }, [setCurrentIdx, loadRecordWithNames]);

  const [searchText, setSearchText] = React.useState('');

  const tabItems = [
    {
      key: '1',
      label: '基本資料',
      children: (
        <Tab1BasicInfo
          formData={formData}
          names={names}
          isEditing={isEditing}
          getField={getField}
          setField={setField}
          resolveSingleName={resolveSingleName}
          getFieldError={getFieldError}
        />
      ),
    },
    {
      key: '2',
      label: '核准',
      children: (
        <Tab2Approval
          formData={formData}
          isEditing={isEditing}
          getField={getField}
          setField={setField}
        />
      ),
    },
  ];

  return (
    <PermissionGuard module={Modules.PW9208} action="query">
      <Spin spinning={loading}>
        <div className="pw9208-form">
          <div className="page-wrapper">
            {/* Title */}
            <div className="page-title-bar">
              <h1>資遣通知單</h1>
              <span className="nav-info">
                {allIds.length > 0 ? `${currentIdx + 1} / ${allIds.length}` : '無資料'}
              </span>
            </div>

            {/* Action Bar */}
            <ActionBar
              mode={mode}
              isEditing={isEditing}
              hasRecord={!!record}
              currentIdx={currentIdx}
              allIdsLength={allIds.length}
              permissions={perms}
              onAdd={handleAdd}
              onModify={handleModify}
              onDelete={handleDeleteRecord}
              onNavTo={handleNavTo}
              onExit={() => navigate('/erp')}
            />

            {/* Search */}
            <div className="search-bar">
              <Input
                prefix={<SearchOutlined />}
                placeholder="搜尋資遣單號/員工代號..."
                allowClear
                style={{ width: 280 }}
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                onPressEnter={() => handleSearch(searchText)}
              />
              <button className="btn" onClick={() => handleSearch(searchText)}>搜尋</button>
            </div>

            {/* Tabs */}
            <Tabs items={tabItems} defaultActiveKey="1" />
          </div>
        </div>
      </Spin>
    </PermissionGuard>
  );
};

export default PW9208Form;
